/**
 * Keep-Alive Service
 * Manages heartbeat polling to prevent UI timeouts during long-running backend operations
 */

class KeepAliveService {
  constructor() {
    this.heartbeatInterval = null;
    this.heartbeatIntervalMs = 30000; // 30 seconds
    this.isActive = false;
    this.operationStartTime = null;
    this.listeners = new Set();
  }

  /**
   * Start heartbeat polling
   * @param {Object} options - Configuration options
   * @param {number} options.intervalMs - Heartbeat interval in milliseconds (default: 30000)
   */
  start(options = {}) {
    if (this.isActive) {
      console.log('[KeepAlive] Heartbeat already active, restarting...');
      this.stop();
    }

    const { intervalMs = this.heartbeatIntervalMs } = options;
    this.heartbeatIntervalMs = intervalMs;
    this.isActive = true;
    this.operationStartTime = Date.now();

    console.log(`[KeepAlive] Starting heartbeat polling (interval: ${intervalMs}ms)`);
    
    // Start immediate heartbeat
    this.sendHeartbeat();
    
    // Set up interval
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, intervalMs);

    this.notifyListeners('started');
  }

  /**
   * Stop heartbeat polling
   */
  stop() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.isActive) {
      console.log('[KeepAlive] Stopping heartbeat polling');
      this.isActive = false;
      this.operationStartTime = null;
      this.notifyListeners('stopped');
    }
  }

  /**
   * Send heartbeat request to backend
   */
  async sendHeartbeat() {
    try {
      const baseUrl = await this.getCurrentBaseUrl();
      const response = await fetch(`${baseUrl}/api/heartbeat`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const elapsed = this.operationStartTime ? Date.now() - this.operationStartTime : 0;
        console.log(`[KeepAlive] Heartbeat successful (elapsed: ${Math.round(elapsed / 1000)}s)`);
        this.notifyListeners('heartbeat', { elapsed, data });
        return true;
      } else {
        console.warn('[KeepAlive] Heartbeat request failed:', response.status);
        this.notifyListeners('error', { status: response.status });
        return false;
      }
    } catch (error) {
      console.error('[KeepAlive] Heartbeat error:', error);
      this.notifyListeners('error', { error: error.message });
      return false;
    }
  }

  /**
   * Get current base URL (same logic as apiService)
   */
  async getCurrentBaseUrl() {
    // Try to get from runtime config first
    if (window.RUNTIME_CONFIG && window.RUNTIME_CONFIG.API_URL) {
      return window.RUNTIME_CONFIG.API_URL;
    }
    
    // Fallback to environment variable or default
    return process.env.REACT_APP_API_URL || 'http://localhost:5001';
  }

  /**
   * Get elapsed time since operation started
   * @returns {number} Elapsed time in milliseconds
   */
  getElapsedTime() {
    if (!this.operationStartTime) return 0;
    return Date.now() - this.operationStartTime;
  }

  /**
   * Check if keep-alive is active
   * @returns {boolean}
   */
  getActive() {
    return this.isActive;
  }

  /**
   * Add event listener
   * @param {Function} callback - Callback function
   */
  addListener(callback) {
    this.listeners.add(callback);
  }

  /**
   * Remove event listener
   * @param {Function} callback - Callback function
   */
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   * @param {string} event - Event name
   * @param {Object} data - Event data
   */
  notifyListeners(event, data = {}) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('[KeepAlive] Listener error:', error);
      }
    });
  }
}

// Export singleton instance
const keepAliveService = new KeepAliveService();
export default keepAliveService;

